import { FC } from "react";
import { useTranslation } from "react-i18next";
import { PublicOrderFormConfig } from "../../../../types/PublicOrderFormConfig";
import { SettingsRowProps } from "../../components/SettingsRow";

interface TextsTabProps {
  SettingsRow: React.ComponentType<SettingsRowProps>;
  config: PublicOrderFormConfig;
  handleConfigChange: <K extends keyof PublicOrderFormConfig>(key: K, value: PublicOrderFormConfig[K]) => void;
}

const TextsTab: FC<TextsTabProps> = ({ SettingsRow, config, handleConfigChange }) => {
  const { t } = useTranslation(["order_form"]);

  return (
    <div className="space-y-6">
      <div>
        <div className="divide-y divide-border">
          <SettingsRow
            title={t("order_form:product_list_flow.flow_title")}
            description={t("order_form:product_list_flow.flow_title_description")}
          >
            <input
              type="text"
              value={config.productListFlowTitle || ""}
              onChange={(e) => handleConfigChange("productListFlowTitle", e.target.value)}
              placeholder={t("order_form:product_list_flow.flow_title_placeholder", "Order")}
              className="w-80 px-3 py-1.5 text-sm border border-gray-300 rounded-md focus:border-brand focus:ring-1 focus:ring-brand dark:border-gray-600 dark:bg-gray-700 dark:text-white"
            />
          </SettingsRow>
          <SettingsRow
            title={t("order_form:product_list_flow.flow_description")}
            description={t("order_form:product_list_flow.flow_description_description")}
          >
            <textarea
              value={config.productListFlowDescription || ""}
              onChange={(e) => handleConfigChange("productListFlowDescription", e.target.value)}
              placeholder={t("order_form:product_list_flow.flow_description_placeholder")}
              rows={2}
              className="w-80 px-3 py-1.5 text-sm border border-gray-300 rounded-md focus:border-brand focus:ring-1 focus:ring-brand dark:border-gray-600 dark:bg-gray-700 dark:text-white resize-none"
            />
          </SettingsRow>
          <SettingsRow
            title={t("order_form:product_list_flow.confirmation_title")}
            description={t("order_form:product_list_flow.confirmation_title_description")}
          >
            <input
              type="text"
              value={config.productListFlowConfirmationTitle || ""}
              onChange={(e) => handleConfigChange("productListFlowConfirmationTitle", e.target.value)}
              placeholder={t("order_form:product_list_flow.confirmation_title_placeholder")}
              className="w-80 px-3 py-1.5 text-sm border border-gray-300 rounded-md focus:border-brand focus:ring-1 focus:ring-brand dark:border-gray-600 dark:bg-gray-700 dark:text-white"
            />
          </SettingsRow>
          <SettingsRow
            title={t("order_form:product_list_flow.confirmation_description")}
            description={t("order_form:product_list_flow.confirmation_description_description")}
          >
            <textarea
              value={config.productListFlowConfirmationDescription || ""}
              onChange={(e) => handleConfigChange("productListFlowConfirmationDescription", e.target.value)}
              placeholder={t("order_form:product_list_flow.confirmation_description_placeholder")}
              rows={2}
              className="w-80 px-3 py-1.5 text-sm border border-gray-300 rounded-md focus:border-brand focus:ring-1 focus:ring-brand dark:border-gray-600 dark:bg-gray-700 dark:text-white resize-none"
            />
          </SettingsRow>
          <SettingsRow
            title={t("order_form:product_list_flow.next_steps")}
            description={t("order_form:product_list_flow.next_steps_description")}
            separator={false}
          >
            <textarea
              value={config.productListFlowConfirmationNextSteps || ""}
              onChange={(e) => handleConfigChange("productListFlowConfirmationNextSteps", e.target.value)}
              placeholder={t("order_form:product_list_flow.next_steps_placeholder")}
              rows={3}
              className="w-80 px-3 py-1.5 text-sm border border-gray-300 rounded-md focus:border-brand focus:ring-1 focus:ring-brand dark:border-gray-600 dark:bg-gray-700 dark:text-white resize-none"
            />
          </SettingsRow>
        </div>
      </div>

      <div>
        <div className="mb-4">
          <h3 className="text-base font-semibold text-text">
            {t("order_form:custom_order_flow.title")}
          </h3>
          <p className="text-sm text-muted mt-1">
            {t("order_form:custom_order_flow.description")}
          </p>
        </div>
        <div className="divide-y divide-border">
          <SettingsRow
            title={t("order_form:custom_order_flow.flow_title")}
            description={t("order_form:custom_order_flow.flow_title_description")}
          >
            <input
              type="text"
              value={config.customOrderFlowTitle || ""}
              onChange={(e) => handleConfigChange("customOrderFlowTitle", e.target.value)}
              placeholder={t("order_form:custom_order_flow.flow_title_placeholder")}
              className="w-80 px-3 py-1.5 text-sm border border-gray-300 rounded-md focus:border-brand focus:ring-1 focus:ring-brand dark:border-gray-600 dark:bg-gray-700 dark:text-white"
            />
          </SettingsRow>
          <SettingsRow
            title={t("order_form:custom_order_flow.flow_description")}
            description={t("order_form:custom_order_flow.flow_description_description")}
          >
            <textarea
              value={config.customOrderFlowDescription || ""}
              onChange={(e) => handleConfigChange("customOrderFlowDescription", e.target.value)}
              placeholder={t("order_form:custom_order_flow.flow_description_placeholder")}
              rows={2}
              className="w-80 px-3 py-1.5 text-sm border border-gray-300 rounded-md focus:border-brand focus:ring-1 focus:ring-brand dark:border-gray-600 dark:bg-gray-700 dark:text-white resize-none"
            />
          </SettingsRow>
          <SettingsRow
            title={t("order_form:custom_order_flow.confirmation_title")}
            description={t("order_form:custom_order_flow.confirmation_title_description")}
          >
            <input
              type="text"
              value={config.customOrderFlowConfirmationTitle || ""}
              onChange={(e) => handleConfigChange("customOrderFlowConfirmationTitle", e.target.value)}
              placeholder={t("order_form:custom_order_flow.confirmation_title_placeholder")}
              className="w-80 px-3 py-1.5 text-sm border border-gray-300 rounded-md focus:border-brand focus:ring-1 focus:ring-brand dark:border-gray-600 dark:bg-gray-700 dark:text-white"
            />
          </SettingsRow>
          <SettingsRow
            title={t("order_form:custom_order_flow.confirmation_description")}
            description={t("order_form:custom_order_flow.confirmation_description_description")}
          >
            <textarea
              value={config.customOrderFlowConfirmationDescription || ""}
              onChange={(e) => handleConfigChange("customOrderFlowConfirmationDescription", e.target.value)}
              placeholder={t("order_form:custom_order_flow.confirmation_description_placeholder")}
              rows={2}
              className="w-80 px-3 py-1.5 text-sm border border-gray-300 rounded-md focus:border-brand focus:ring-1 focus:ring-brand dark:border-gray-600 dark:bg-gray-700 dark:text-white resize-none"
            />
          </SettingsRow>
          <SettingsRow
            title={t("order_form:custom_order_flow.next_steps")}
            description={t("order_form:custom_order_flow.next_steps_description")}
            separator={false}
          >
            <textarea
              value={config.customOrderFlowConfirmationNextSteps || ""}
              onChange={(e) => handleConfigChange("customOrderFlowConfirmationNextSteps", e.target.value)}
              placeholder={t("order_form:custom_order_flow.next_steps_placeholder")}
              rows={3}
              className="w-80 px-3 py-1.5 text-sm border border-gray-300 rounded-md focus:border-brand focus:ring-1 focus:ring-brand dark:border-gray-600 dark:bg-gray-700 dark:text-white resize-none"
            />
          </SettingsRow>
        </div>
      </div>
    </div>
  );
};

export default TextsTab;


